import { compact } from "lodash";
import { useMemo } from "react";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { ConsultantSearchCardDataFragment, GDPRConsentType } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { ConsultantTableColumns } from "@bonliva-crm/shared";
import { TextWrapRenderer } from "@bonliva-crm/ui";
import { EmailRenderer } from "./renderers/email-renderer";
import { PhoneRenderer } from "./renderers/phone-renderer";
import { CommentSearchResultRenderer } from "./renderers/comment-search-result-renderer";
import { ReferenceSwedenRenderer } from "./renderers/reference-sweden-renderer";
import { NpReferenceSwedenRenderer } from "./renderers/np-reference-sweden-renderer";
import { BasicQualityCheckRenderer } from "./renderers/basic-quality-check-renderer";
import { SaHlrRenderer } from "./renderers/sa-hlr-renderer";
import { ConsultantRowData } from "../../utils/consultant-row-data";
import { CriminalCheckRenderer } from "./renderers/criminal-check-renderer";
import { CustomCellRendererProps } from "ag-grid-react";
import { CallListStatsRenderer } from "./renderers/call-list-stats-renderer";
import { FavouriteStarRenderer } from "./renderers/favourite-star-renderer";
import { GdprJobMailingRenderer } from "./renderers/gdpr-job-mailing-renderer";
import { TagsRenderer } from "@bonliva-crm/ui";
import { MarkHighlighter, WithCopyToClipboard } from "@common";
import { QualityNoteRenderer } from "./renderers/quality-note-renderer";
import { cellRendererLink } from "@common/renderers";
import { ConsultantProfileLocation } from "@navigation/locations";

export function useInitialColumnDefs(
  withComments: boolean
): Array<ColDef<ConsultantRowData>> {
  const defs = useMemo(() => {
    const result: Array<ColDef> = [];

    result.push({
      field: "favourite" as any,
      headerName: "",
      pinned: "left",
      suppressMovable: true,
      lockPinned: true,
      initialPinned: true,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      sortable: false,
      resizable: false,
      cellRenderer: FavouriteStarRenderer,
      cellStyle: {
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }
    });

    result.push({
      field: ConsultantTableColumns.Name,
      headerName: locale.consultantTableColumnType.Name,
      pinned: "left",
      suppressMovable: true,
      lockPinned: true,
      initialPinned: true,
      wrapText: true,
      sortable: true,
      cellRenderer: (props: CustomCellRendererProps<ConsultantRowData>) => {
        return (
          <MarkHighlighter
            highlightSearch={props.context?.highlightText}
            getMarkjsContext={() => props.eGridCell}
          >
            <WithCopyToClipboard contentToCopy={props.data?.Name}>
              {props.data?.Name}
            </WithCopyToClipboard>
          </MarkHighlighter>
        );
      }
    });

    if (withComments) {
      result.push({
        field: ConsultantTableColumns.Comments,
        headerName: locale.Comments,
        cellRenderer: CommentSearchResultRenderer,
        suppressMovable: true,
        minWidth: 250,
        initialHide: true,
        sortable: false,
        pinned: "left",
        lockPinned: true,
        initialPinned: true,
        wrapText: true
      });
    }

    result.push({
      field: ConsultantTableColumns.Phone,
      sortIndex: 10,
      initialSortIndex: 10,
      headerName: locale.consultantTableColumnType.Phone,
      cellRenderer: (props) => (
        <MarkHighlighter
          highlightSearch={props.context?.highlightText}
          getMarkjsContext={() => props.eGridCell}
        >
          <PhoneRenderer {...props} />
        </MarkHighlighter>
      ),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.Email,
      headerName: locale.consultantTableColumnType.Email,
      cellRenderer: (props) => (
        <MarkHighlighter
          highlightSearch={props.context?.highlightText}
          getMarkjsContext={() => props.eGridCell}
        >
          <EmailRenderer {...props} />
        </MarkHighlighter>
      ),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.SocialSecurityNo,
      headerName: locale.consultantTableColumnType.SocialSecurityNo,
      wrapText: true,
      sortable: false,
      cellRenderer: (props: CustomCellRendererProps<ConsultantRowData>) => {
        return (
          <MarkHighlighter
            highlightSearch={props.context?.highlightText}
            getMarkjsContext={() => props.eGridCell}
          >
            <WithCopyToClipboard contentToCopy={props.data?.SocialSecurityNo}>
              {props.data?.SocialSecurityNo}
            </WithCopyToClipboard>
          </MarkHighlighter>
        );
      }
    });

    result.push({
      field: ConsultantTableColumns.Availability,
      headerName: locale.consultantTableColumnType.Availability,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "Availability">({
          props,
          property: "Availability",
          valueConverter: (value) =>
            value?.ranges.map(
              (range) =>
                `V. ${moment.utc(range.from).format("WW YYYY")} - V. ${moment
                  .utc(range.to)
                  .format("WW YYYY")}`
            )
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.LastContactDate,
      headerName: locale.consultantTableColumnType.LastContactDate,
      valueGetter: (params) =>
        params.data?.LastContactDate
          ? moment(params.data.LastContactDate).format("YYYY-MM-DD")
          : "",
      wrapText: true,
      sortable: true
    });

    result.push({
      field: ConsultantTableColumns.AreasOfExpertise,
      headerName: locale.consultantTableColumnType.AreasOfExpertise,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "AreasOfExpertise">({
          props,
          property: "AreasOfExpertise"
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.ResponsiblePerson,
      headerName: locale.consultantTableColumnType.ResponsiblePerson,
      valueGetter: (params) => params.data?.ResponsiblePerson?.Name,
      wrapText: true,
      sortable: true
    });

    result.push({
      field: ConsultantTableColumns.Address,
      headerName: locale.consultantTableColumnType.Address,
      valueGetter: (params) => params.data?.Address?.City,
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.Specializations,
      headerName: locale.consultantTableColumnType.Specializations,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "Specializations">({
          props,
          property: "Specializations"
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: "Expertise",
      headerName: locale.consultantTableColumnType.Systems,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "expertise">({
          props,
          property: "expertise",
          valueConverter: (value) => compact(value?.systems)
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.AvailableForWorkIn,
      headerName: locale.consultantTableColumnType.AvailableForWorkIn,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "AvailableForWorkInCountries">({
          props,
          property: "AvailableForWorkInCountries",
          valueConverter: (value) =>
            value?.map((country) => locale.countries[country]) ?? []
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.GDPRMarketing,
      headerName: locale.consultantTableColumnType.GDPRMarketing,
      valueGetter: (params) =>
        params.data?.GDPRConsentRecords.find(
          (record) => record.type === GDPRConsentType.Marketing && record.granted
        )
          ? locale.Yes
          : locale.No,
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.GDPRIntegrationPolicy,
      headerName: locale.consultantTableColumnType.GDPRIntegrationPolicy,
      valueGetter: (params) =>
        params.data?.GDPRConsentRecords.find(
          (record) => record.type === GDPRConsentType.IntegrationPolicy && record.granted
        )
          ? locale.Yes
          : locale.No,
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.GDPRJobMailing,
      headerName: locale.consultantTableColumnType.GDPRJobMailing,
      cellRenderer: GdprJobMailingRenderer,
      minWidth: 150,
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.GDPRLegalBasis,
      headerName: locale.consultantTableColumnType.GDPRLegalBasis,
      valueGetter: (params) =>
        params.data?.LegalBasis ? locale.legalBasisType[params.data?.LegalBasis] : "",
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.HasDrivingLicense,
      headerName: locale.consultantTableColumnType.HasDrivingLicense,
      valueGetter: (params) => {
        if (params.data?.hasDrivingLicence) {
          if (params.data?.drivingLicenceType) {
            return `${locale.Yes} – ${
              locale.drivingLicenceType[params.data.drivingLicenceType]
            }`;
          }

          return locale.Yes;
        }

        return locale.No;
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.OtherEducations,
      headerName: locale.consultantTableColumnType.OtherEducations,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "expertise">({
          props,
          property: "expertise",
          valueConverter: (x) => compact(x?.educations)
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.MethodsAndInstruments,
      headerName: locale.consultantTableColumnType.MethodsAndInstruments,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "expertise">({
          props,
          property: "expertise",
          valueConverter: (x) => compact(x?.methods)
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.Machines,
      headerName: locale.consultantTableColumnType.Machines,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "expertise">({
          props,
          property: "expertise",
          valueConverter: (x) => compact(x?.machines)
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.InterestedToWorkIn,
      headerName: locale.consultantTableColumnType.InterestedToWorkIn,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData, "generalInterest">({
          props,
          property: "generalInterest",
          valueConverter: (value) => {
            if (!value?.regions.length) {
              return [];
            }

            const groupedRegions: Record<string, string[]> = {};

            value.regions.forEach((countryWithRegion) => {
              const [country, region] = countryWithRegion.split(":");
              if (groupedRegions[country]) {
                groupedRegions[country].push(region || "*");
              } else {
                groupedRegions[country] = [region || "*"];
              }
            });

            return Object.entries(groupedRegions).map(([country, regions]) => {
              return `${locale.countries[country]}${
                regions[0] === "*" ? "" : `: ${regions.join(", ")}`
              }`;
            });
          }
        }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.WorksForCompetitors,
      headerName: locale.consultantTableColumnType.WorksForCompetitors,
      cellRenderer: (props) =>
        TextWrapRenderer<ConsultantRowData>({ props, property: "worksForCompetitors" }),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.IsQualityApproved,
      headerName: locale.consultantTableColumnType.IsQualityApproved,
      valueGetter: (params) => {
        if (!params.data?.isQualityApproved) {
          return locale.IsNotQualified;
        }

        if (params.data?.isQualityExpired) {
          return locale.IsQualifiedAndExpired;
        }

        return locale.IsQualified;
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.IsNationalQualityApproved,
      headerName: locale.consultantTableColumnType.IsNationalQualityApproved,
      valueGetter: (params) => {
        const presentationApproved = params.data?.isPresentationReadyQualityApproved;
        const presentationExpired = params.data?.isPresentationReadyQualityExpired;
        const nationalApproved = params.data?.isNationalQualityApproved;
        const nationalExpired = params.data?.isNationalQualityExpired;

        if (nationalApproved && !nationalExpired) return locale.IsQualified;

        if (presentationApproved && !presentationExpired) return locale.PresentationReady;

        if (nationalApproved && nationalExpired) return locale.IsQualifiedAndExpired;

        return locale.IsNotQualified;
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: ConsultantTableColumns.SwedenCVAudit,
      headerName: locale.consultantTableColumnType.SwedenCVAudit,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="CV-granskning" />
      ),
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenReferences,
      headerName: locale.consultantTableColumnType.SwedenReferences,
      cellRenderer: ReferenceSwedenRenderer,
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenNationalReferences,
      headerName: locale.consultantTableColumnType.SwedenNationalReferences,
      cellRenderer: NpReferenceSwedenRenderer,
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.IVO,
      headerName: locale.consultantTableColumnType.IVO,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="IVO-Uppdatering" />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.IVOErrands,
      headerName: locale.consultantTableColumnType.IVOErrands,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="IVO-Ärenden" />
      ),
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.HOSP,
      headerName: locale.consultantTableColumnType.HOSP,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="HOSP" />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenHealthDeclaration,
      headerName: locale.consultantTableColumnType.SwedenHealthDeclaration,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="Hälsodeklaration" />
      ),
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenProofOfEducation,
      headerName: locale.consultantTableColumnType.SwedenProofOfEducation,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="Utbildningsbevis" />
      ),
      wrapText: true,
      sortable: false,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenProofOfWork,
      headerName: locale.consultantTableColumnType.SwedenProofOfWork,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="Yrkesbevis" />
      ),
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenCriminalRecord,
      headerName: locale.consultantTableColumnType.SwedenCriminalRecord,
      cellRenderer: (p) => (
        <CriminalCheckRenderer {...p} country="Sweden" itemName="Belastningsregister" />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenNpCriminalRecord,
      headerName: locale.consultantTableColumnType.SwedenNpCriminalRecord,
      cellRenderer: (p) => (
        <CriminalCheckRenderer
          {...p}
          country="Sweden"
          itemName="Belastningsregister Nationella"
        />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenBusinessIntelligence,
      headerName: locale.consultantTableColumnType.SwedenBusinessIntelligence,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="Omvärldsbevakning" />
      ),
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenHSAIDSITHS,
      headerName: locale.consultantTableColumnType.SwedenHSAIDSITHS,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer
          {...p}
          country="Sweden"
          itemName="HSAID/SITHS"
          withComment
        />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenIdCheck,
      headerName: locale.consultantTableColumnType.SwedenIdCheck,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="ID-kontroll" />
      ),
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenInterviewDone,
      headerName: locale.consultantTableColumnType.SwedenInterviewDone,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer
          {...p}
          country="Sweden"
          itemName="Intervju genomförd"
        />
      ),
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenLanguageCheck,
      headerName: locale.consultantTableColumnType.SwedenLanguageCheck,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="Språkkontroll" />
      ),
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenHLR,
      headerName: locale.consultantTableColumnType.SwedenHLR,
      cellRenderer: (p) => (
        <BasicQualityCheckRenderer {...p} country="Sweden" itemName="HLR" />
      ),
      wrapText: true,
      sortable: true,

      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.SwedenSAHLR,
      headerName: locale.consultantTableColumnType.SwedenSAHLR,
      cellRenderer: SaHlrRenderer,
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.CallListStats,
      headerName: locale.consultantTableColumnType.CallListStats,
      cellRenderer: CallListStatsRenderer,
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.Tags,
      headerName: locale.consultantTableColumnType.Tags,
      cellRenderer: (props) => <TagsRenderer tags={props.data?.tags} />,
      wrapText: true,
      cellStyle: { padding: 0 }
    });

    result.push({
      field: ConsultantTableColumns.NextContactDate,
      headerName: locale.consultantTableColumnType.NextContactDate,
      valueGetter: (params) =>
        params.data?.nextContactDate?.date
          ? moment(params.data.nextContactDate.date).format("YYYY-MM-DD")
          : "",
      wrapText: true
    });

    result.push({
      field: ConsultantTableColumns.PipelineStatus,
      headerName: locale.consultantTableColumnType.PipelineStatus,
      valueGetter: (params) =>
        locale.consultantPipelineBoardHeaders[params.data?.pipelineStatus] ?? "",
      wrapText: true
    });

    result.push({
      field: ConsultantTableColumns.BookingStatus,
      headerName: locale.consultantTableColumnType.BookingStatus,
      valueGetter: (params) =>
        params.data?.bookingStatus
          ? locale.consultantBookingStatus[params.data.bookingStatus]
          : "",
      wrapText: true
    });

    result.push({
      field: ConsultantTableColumns.QualityNote,
      headerName: locale.consultantTableColumnType.QualityNote,
      cellRenderer: QualityNoteRenderer,
      wrapText: true
    });

    result.push({
      field: ConsultantTableColumns.CreatedAt,
      headerName: locale.consultantTableColumnType.CreatedAt,
      valueGetter: (params) =>
        params.data?.createdAt ? moment(params.data.createdAt).format("YYYY-MM-DD") : "",
      wrapText: true,
      sortable: true
    });

    result.push({
      field: ConsultantTableColumns.UpdatedAt,
      headerName: locale.consultantTableColumnType.UpdatedAt,
      valueGetter: (params) =>
        params.data?.updatedAt ? moment(params.data.updatedAt).format("YYYY-MM-DD") : "",
      wrapText: true,
      sortable: true
    });

    const resultWithLinks = result.map((colDef) => {
      return {
        ...colDef,
        cellRenderer: cellRendererLink(colDef.cellRenderer, (params) => {
          const id = (params.data as ConsultantSearchCardDataFragment)?._id;
          const location = new ConsultantProfileLocation(id);

          return {
            to: location.toURI(),
            onClick: (e) => e.preventDefault()
          };
        })
      };
    }) as Array<ColDef<ConsultantRowData>>;

    return resultWithLinks;
  }, [withComments]);

  return defs;
}
